// Drill Data Management System
// Systematic naming convention: drill_{originalQuestionId}_{drillNumber}

// In-memory storage for drill data
const drillStorage = new Map();

// Drill validation and submission
export const submitDrillAnswer = (originalQuestionId, drillQuestionId, answer) => {
  try {
    // Get or create drill results for this original question
    let drillResults = drillStorage.get(originalQuestionId);
    if (!drillResults) {
      drillResults = {
        originalQuestionId,
        drillAnswers: {},
        totalDrills: 5,
        answeredDrills: 0,
        correctAnswers: 0,
        isComplete: false,
        percentage: 0,
        timestamp: new Date().toISOString()
      };
    }

    // Store the answer
    drillResults.drillAnswers[drillQuestionId] = answer;
    drillResults.answeredDrills = Object.keys(drillResults.drillAnswers).length;

    // Calculate results
    drillResults.isComplete = drillResults.answeredDrills >= drillResults.totalDrills;
    
    // Save to storage
    drillStorage.set(originalQuestionId, drillResults);

    // Also save to localStorage for persistence
    localStorage.setItem(`drill_${originalQuestionId}`, JSON.stringify(drillResults));

    return true;
  } catch (error) {
    console.error('Error submitting drill answer:', error);
    return false;
  }
};

// Get drill results
export const getDrillResults = (originalQuestionId) => {
  try {
    // Try to get from memory first
    let drillResults = drillStorage.get(originalQuestionId);
    
    // If not in memory, try localStorage
    if (!drillResults) {
      const stored = localStorage.getItem(`drill_${originalQuestionId}`);
      if (stored) {
        drillResults = JSON.parse(stored);
        drillStorage.set(originalQuestionId, drillResults);
      }
    }

    if (!drillResults) return null;

    // Calculate percentage and correct answers
    const correctAnswers = Object.values(drillResults.drillAnswers).filter(answer => {
      // This would need to be validated against the actual correct answers
      // For now, we'll use a simple calculation
      return answer && answer.length > 0;
    }).length;

    drillResults.correctAnswers = correctAnswers;
    drillResults.percentage = Math.round((correctAnswers / drillResults.totalDrills) * 100);

    return drillResults;
  } catch (error) {
    console.error('Error getting drill results:', error);
    return null;
  }
};

// Enhanced drill results with proper scoring
export const getEnhancedDrillResults = (originalQuestionId, drillQuestions) => {
  try {
    // Get basic drill results
    let drillResults = getDrillResults(originalQuestionId);
    
    if (!drillResults || !drillQuestions) return null;

    // Calculate correct answers by comparing user answers with actual correct answers
    let correctAnswers = 0;
    const userAnswers = drillResults.drillAnswers || {};
    
    drillQuestions.forEach(drillQuestion => {
      const userAnswer = userAnswers[drillQuestion.id];
      if (!userAnswer) return; // No answer provided
      
      // For essay-type questions, compare with answer field
      if (drillQuestion.isEssayType || !drillQuestion.options || drillQuestion.options.length === 0) {
        const correctAnswer = drillQuestion.answer || drillQuestion.correctAnswer || '';
        if (userAnswer.trim() === correctAnswer.trim()) {
          correctAnswers++;
        }
      } else {
        // For MC questions, compare with correctAnswer
        if (userAnswer === drillQuestion.correctAnswer) {
          correctAnswers++;
        }
      }
    });

    // Update results with accurate scoring
    drillResults.correctAnswers = correctAnswers;
    drillResults.percentage = Math.round((correctAnswers / drillResults.totalDrills) * 100);
    drillResults.isComplete = drillResults.answeredDrills >= drillResults.totalDrills;

    return drillResults;
  } catch (error) {
    console.error('Error getting enhanced drill results:', error);
    return null;
  }
};

// Clear drill results
export const clearDrillResults = (originalQuestionId) => {
  try {
    drillStorage.delete(originalQuestionId);
    localStorage.removeItem(`drill_${originalQuestionId}`);
    return true;
  } catch (error) {
    console.error('Error clearing drill results:', error);
    return false;
  }
};

// Get all drill results
export const getAllDrillResults = () => {
  try {
    const results = [];
    for (const [originalQuestionId, drillResults] of drillStorage) {
      results.push({
        originalQuestionId,
        ...drillResults
      });
    }
    return results;
  } catch (error) {
    console.error('Error getting all drill results:', error);
    return [];
  }
};

// Validate drill question structure
export const validateDrillQuestion = (drillQuestion) => {
  // Check if this is an essay-type question (no options)
  const isEssayType = drillQuestion.isEssayType || 
                     !drillQuestion.options || 
                     !Array.isArray(drillQuestion.options) || 
                     drillQuestion.options.length === 0;
  
  // Ensure id exists - generate if missing
  if (!drillQuestion.id) {
    drillQuestion.id = drillQuestion.id || 
                      `drill_${drillQuestion.originalQuestionId || 'unknown'}_${drillQuestion.questionNumber || Date.now()}`;
  }
  
  // Required fields for all questions
  const baseRequiredFields = ['id', 'question', 'solution'];
  const missingBaseFields = baseRequiredFields.filter(field => !drillQuestion[field]);
  
  if (missingBaseFields.length > 0) {
    console.warn(`Missing required fields in drill question: ${missingBaseFields.join(', ')}`);
    return false;
  }
  
  if (isEssayType) {
    // Essay-type question: requires answer field instead of options/correctAnswer
    if (!drillQuestion.answer && !drillQuestion.correctAnswer) {
      console.warn('Essay-type drill question missing answer field');
      return false;
    }
    return true; // Essay questions don't need options validation
  }
  
  // MC question: requires options and correctAnswer
  if (!drillQuestion.options || !Array.isArray(drillQuestion.options)) {
    console.warn('MC drill question missing options array');
    return false;
  }
  
  if (drillQuestion.options.length !== 4) {
    console.warn('MC drill question must have exactly 4 options');
    return false;
  }

  if (!drillQuestion.correctAnswer) {
    console.warn('MC drill question missing correctAnswer');
    return false;
  }

  // Validate correct answer exists in options
  const optionIds = drillQuestion.options.map(opt => opt.id);
  if (!optionIds.includes(drillQuestion.correctAnswer)) {
    console.warn(`Correct answer ${drillQuestion.correctAnswer} not found in options`);
    return false;
  }

  return true;
};

// Enhanced validation for primary school questions
export const validatePrimaryDrillQuestion = (drillQuestion) => {
  // Basic validation
  if (!validateDrillQuestion(drillQuestion)) {
    return false;
  }

  // Additional validation for primary school content
  if (!drillQuestion.question || drillQuestion.question.trim() === '') {
    console.warn('Primary drill question has empty question text');
    return false;
  }

  if (!drillQuestion.solution || drillQuestion.solution.trim() === '') {
    console.warn('Primary drill question has empty solution');
    return false;
  }

  // Validate that the question is appropriate for primary school level
  const questionText = drillQuestion.question.toLowerCase();
  const solutionText = drillQuestion.solution.toLowerCase();
  
  // Check for appropriate primary school content
  const primarySchoolKeywords = [
    'apple', 'candy', 'toy', 'ball', 'book', 'pencil', 'pen', 'paper',
    'count', 'number', 'add', 'subtract', 'multiply', 'divide',
    'shape', 'circle', 'square', 'triangle', 'rectangle',
    'money', 'dollar', 'cent', 'coin',
    'time', 'hour', 'minute', 'clock',
    'length', 'width', 'height', 'cm', 'm', 'km',
    'weight', 'kg', 'g', 'pound'
  ];

  const hasPrimarySchoolContent = primarySchoolKeywords.some(keyword => 
    questionText.includes(keyword) || solutionText.includes(keyword)
  );

  if (!hasPrimarySchoolContent) {
    console.warn('Primary drill question may not be appropriate for primary school level');
    // Don't fail validation, just warn
  }

  return true;
};

// Generate systematic drill data for testing
export const generateSystematicDrillData = (originalQuestion, numberOfDrills = 5) => {
  const drillQuestions = [];
  const { id: originalId, topic, difficulty } = originalQuestion;

  for (let i = 1; i <= numberOfDrills; i++) {
    const drillId = `drill_${originalId}_${i}`;
    
    // Create systematic variations based on topic
    const variation = generateTopicSpecificVariation(topic, i);
    
    const drillQuestion = {
      id: drillId,
      questionNumber: i,
      question: applySystematicVariation(originalQuestion.question, variation),
      options: originalQuestion.options.map((option, index) => ({
        id: option.id,
        text: applySystematicVariation(option.text, variation)
      })),
      correctAnswer: originalQuestion.correctAnswer,
      solution: applySystematicVariation(originalQuestion.solution, variation),
      topic: topic,
      difficulty: difficulty,
      hasImage: false,
      imageDescription: "",
      isDrillQuestion: true,
      originalQuestionId: originalId,
      metadata: {
        generatedAt: new Date().toISOString(),
        variationType: variation.type,
        variationParams: variation.params
      }
    };

    drillQuestions.push(drillQuestion);
  }

  return {
    drillQuestions,
    originalQuestionId: originalId,
    totalDrills: numberOfDrills,
    metadata: {
      generatedAt: new Date().toISOString(),
      isSystematicData: true,
      originalQuestion: {
        id: originalId,
        topic,
        difficulty
      }
    }
  };
};

// Generate topic-specific variations
const generateTopicSpecificVariation = (topic, drillNumber) => {
  const baseVariations = {
    '聯立方程': {
      type: 'linear_equation',
      params: { multiplier: 1 + drillNumber * 0.5, addition: drillNumber * 2 }
    },
    '三角學': {
      type: 'trigonometry',
      params: { angle: [30, 45, 60, 90, 120][drillNumber - 1] || 30 }
    },
    '二次方程': {
      type: 'quadratic',
      params: { coefficient: drillNumber, constant: drillNumber * 3 }
    },
    '幾何': {
      type: 'geometry',
      params: { scale: 1 + drillNumber * 0.2, offset: drillNumber }
    },
    '統計': {
      type: 'statistics',
      params: { dataPoints: 5 + drillNumber * 2, range: 10 + drillNumber * 5 }
    }
  };

  return baseVariations[topic] || {
    type: 'generic',
    params: { multiplier: 1 + drillNumber * 0.3, addition: drillNumber }
  };
};

// Apply systematic variation to text
const applySystematicVariation = (text, variation) => {
  if (!text || typeof text !== 'string') return text;

  switch (variation.type) {
    case 'linear_equation':
      return text.replace(/\d+/g, (match) => {
        const num = parseInt(match);
        return Math.round(num * variation.params.multiplier + variation.params.addition);
      });

    case 'trigonometry':
      return text.replace(/\d+°/g, `${variation.params.angle}°`)
                 .replace(/sin\((\d+)\)/g, `sin(${variation.params.angle})`)
                 .replace(/cos\((\d+)\)/g, `cos(${variation.params.angle})`)
                 .replace(/tan\((\d+)\)/g, `tan(${variation.params.angle})`);

    case 'quadratic':
      return text.replace(/x\^2/g, `${variation.params.coefficient}x^2`)
                 .replace(/\+ (\d+)/g, `+ ${variation.params.constant}`);

    case 'geometry':
      return text.replace(/\d+/g, (match) => {
        const num = parseInt(match);
        return Math.round(num * variation.params.scale + variation.params.offset);
      });

    case 'statistics':
      return text.replace(/\d+/g, (match) => {
        const num = parseInt(match);
        return Math.round(num + variation.params.range);
      });

    default:
      return text.replace(/\d+/g, (match) => {
        const num = parseInt(match);
        return Math.round(num * variation.params.multiplier + variation.params.addition);
      });
  }
};

// Export for use in other modules
export default {
  submitDrillAnswer,
  getDrillResults,
  clearDrillResults,
  getAllDrillResults,
  validateDrillQuestion,
  generateSystematicDrillData
}; 